// TagDetailPanel — the right pane of the Tags page. When a tag is focused
// in the middle list, its settings and actions render here inside a
// resizable panel (TagsPage owns the ResizablePanelGroup):
//   - Header: path, live connectivity, classification and archived badges.
//   - History: acquisition controls (TagAcquisitionPanel).
//   - Footer: classification, archive and delete actions.
//
// Styled in Tailwind + shadcn primitives (per AXI-3524).

import { useState } from 'react'
import { Loader2, X as XIcon } from 'lucide-react'
import {
  type PlantTagIdValue,
  type TagClassificationRecord,
  type TagLlmRole,
  type TagRecord,
  type TagScrapeStatusRecord
} from '@axilon-apps/domains-ontology-ui-state'
import TagClassificationDialog from './TagClassificationDialog'
import TagAcquisitionPanel from './TagAcquisitionPanel'
import { deriveHistorizationStatus } from './historization-status'
import {
  connectivityPillClass,
  liveConnectivityDescription,
  liveConnectivityLabel,
  type Connectivity
} from './connectivity'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { Separator } from '../../components/ui/separator'
import { cn } from '../../lib/utils'

export interface TagDetailPanelProps {
  tag: TagRecord
  /** Graph-owned tag path, when the physical registry path differs. */
  graphTagPath?: string
  /** Graph Tag URN used for every graph read and mutation. */
  graphNodeId?: string
  /** Whether graph mutations are safe for this row's ID-backed graph match. */
  graphActionsEnabled?: boolean
  /** Inventory registration posture retained with the focused row. */
  graphOnly?: boolean
  registrationVendor?: string
  registrationProtocol?: string
  acquisitionQuarantined?: boolean
  gatewayHistoryEnabled?: boolean
  /** A PI Web API sidecar serves this tag's history. */
  piHistory?: boolean
  /** A Canary Views sidecar serves this tag's history. */
  canaryHistory?: boolean
  // Scrape status is optional — a freshly-registered tag may not
  // have a status row yet. The connectivity chip + "Last scrape"
  // row degrade gracefully when null.
  status?: TagScrapeStatusRecord
  // Pre-derived connectivity bucket — kept in lockstep with the
  // sidebar's status dots so the chip color matches.
  connectivity: Connectivity
  /** Current node-ID live read for this tag, when available. */
  liveReading?: PlantTagIdValue
  /** Latest failed read's message, when `liveReading` is the last value kept through it. */
  liveFailure?: string
  // Fires when the panel's close affordance is clicked. The parent
  // decides whether that means "deselect" or just "hide".
  onClose?: () => void
  // Wired by the parent to the existing delete confirmation flow.
  onDelete?: (tag: TagRecord) => void
  // LLM-inferred semantic classification for this tag (from the graph
  // :Tag node's llm_* properties), fetched in bulk by TagsPage. Absent
  // until the tag has been classified.
  classification?: TagClassificationRecord
  // Fires after a classification is created/updated from this panel
  // (classification dialog save) so the parent can refetch
  // its classifications map.
  onClassificationSaved?: () => void
  // Whether this tag is archived (marked not relevant). Drives the footer
  // button's Archive/Unarchive label.
  archived?: boolean
  // Wired by the parent to the archive mutation + archived-set refetch.
  // Rejections render as an inline error above the footer.
  onToggleArchive?: (tag: TagRecord) => Promise<void>
  /** Refreshes inventory and runtime state after acquisition changes. */
  onAcquisitionChanged?: () => void
  /** Opens graph-backed acquisition registration for an unregistered tag. */
  onRegisterAcquisition?: () => void
  /** Opens source conversion for this registry row. */
  onChangeSource?: () => void
  /** Whether collection enablement and cadence controls may be changed. */
  canConfigureHistory?: boolean
  /** Whether classification mutations are available to this caller. */
  classificationEnabled?: boolean
  /** States seen in the inventory summary, used to prefill the classification dialog. */
  observedStates?: string[]
}

// Human labels for the LLM-inferred semantic role (numeric tags only).
const TAG_ROLE_LABEL: Record<TagLlmRole, string> = {
  accumulator: 'Accumulator',
  sensor: 'Sensor',
  setpoint: 'Setpoint',
  control_output: 'Control output',
  identifier_code: 'Identifier / code',
  other: 'Other'
}

// Human label for the LLM class, shown in the header badge when the
// classification has no role.
const LLM_CLASS_LABEL: Record<TagClassificationRecord['tagClass'], string> = {
  numeric: 'Numeric',
  boolean_enum: 'Boolean / Enum',
  other: 'Other'
}

const TagDetailPanel = ({
  tag,
  graphTagPath,
  graphNodeId,
  graphActionsEnabled = true,
  graphOnly = false,
  registrationVendor,
  registrationProtocol,
  acquisitionQuarantined = false,
  gatewayHistoryEnabled = false,
  piHistory = false,
  canaryHistory = false,
  status,
  connectivity,
  liveReading,
  liveFailure,
  onClose,
  onDelete,
  classification,
  onClassificationSaved,
  archived = false,
  onToggleArchive,
  onAcquisitionChanged,
  onRegisterAcquisition,
  onChangeSource,
  canConfigureHistory = true,
  classificationEnabled = true,
  observedStates
}: TagDetailPanelProps) => {
  const [classifyDialogOpen, setClassifyDialogOpen] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const graphIdentityTag =
    graphTagPath && graphTagPath !== tag.tagPath
      ? { ...tag, tagPath: graphTagPath, nodeId: graphNodeId ?? tag.nodeId }
      : { ...tag, nodeId: graphNodeId ?? tag.nodeId }

  const historization = deriveHistorizationStatus({
    graphOnly,
    enabled: tag.state !== 'disabled',
    acquisitionQuarantined,
    registrationProtocol,
    sourceType: tag.sourceType,
    piHistory,
    canaryHistory,
    gatewayHistoryEnabled
  })

  const handleToggleArchive = async () => {
    if (archiving || !graphActionsEnabled || !onToggleArchive) return
    setArchiving(true)
    setArchiveError(null)
    try {
      await onToggleArchive(tag)
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : String(err))
    } finally {
      setArchiving(false)
    }
  }

  return (
    <aside
      className="flex h-full w-full flex-col border-l border-line bg-bg-elev"
      aria-label="Tag detail"
      data-testid="tag-detail-panel"
    >
      <div className="px-4.5 pb-2.5 pt-3.5">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div
              className="truncate font-mono text-14 font-semibold text-ink"
              title={graphTagPath || tag.displayLabel || tag.tagPath}
            >
              {graphTagPath || tag.displayLabel || tag.tagPath}
            </div>
            <div className="mt-px truncate font-mono text-11 text-ink-3" title={tag.tagPath}>
              {tag.tagPath}
            </div>
          </div>
          {onClose ? (
            <Button
              size="icon"
              variant="ghost"
              onClick={onClose}
              aria-label="Close detail panel"
              className="size-5.5"
            >
              <XIcon size={14} />
            </Button>
          ) : null}
        </div>

        <div className="mt-3 flex items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-11 font-semibold',
              connectivityPillClass(connectivity)
            )}
            title={liveFailure ?? liveReading?.error ?? liveConnectivityDescription(connectivity)}
            data-testid={`tag-detail-connectivity-${tag.id}`}
          >
            <span className="size-1.5 rounded-full bg-current" />
            {liveConnectivityLabel(connectivity)}
          </span>
          <Badge
            variant="outline"
            className={cn(
              'text-10',
              historization.kind === 'not_historized' && 'border-st-warn/40 text-st-warn'
            )}
            title={historization.reason}
            data-testid="tag-detail-historization"
            data-historization={historization.kind}
          >
            {historization.kind === 'not_historized'
              ? historization.label
              : `History: ${historization.label}`}
          </Badge>
          {classification ? (
            <Badge
              variant="outline"
              className="font-mono text-10"
              data-testid="tag-detail-class-badge"
            >
              {classification.role !== null
                ? TAG_ROLE_LABEL[classification.role]
                : LLM_CLASS_LABEL[classification.tagClass]}
              {classification.model === 'manual' ? ' · manual' : ''}
            </Badge>
          ) : null}
          {archived ? (
            <Badge
              variant="outline"
              className="font-mono text-10 text-ink-3"
              data-testid="tag-detail-archived-badge"
            >
              Archived
            </Badge>
          ) : null}
        </div>
      </div>

      <Separator />

      <div
        className="min-h-0 flex-1 overflow-auto px-4.5 py-3"
        data-testid="tag-acquisition-content"
      >
        <TagAcquisitionPanel
          tag={tag}
          graphOnly={graphOnly}
          registrationVendor={registrationVendor}
          registrationProtocol={registrationProtocol}
          acquisitionQuarantined={acquisitionQuarantined}
          gatewayHistoryEnabled={gatewayHistoryEnabled}
          piHistory={piHistory}
          canaryHistory={canaryHistory}
          status={status}
          connectivity={connectivity}
          lastReadingAt={liveReading?.error ? undefined : liveReading?.timestamp}
          onChanged={onAcquisitionChanged}
          onRegister={graphOnly ? onRegisterAcquisition : undefined}
          onChangeSource={graphOnly ? undefined : onChangeSource}
          canConfigureHistory={canConfigureHistory}
        />
      </div>

      {archiveError ? (
        <p
          className="m-0 border-t border-line px-4.5 py-1.5 text-12 text-st-down"
          data-testid="tag-archive-error"
        >
          {archiveError}
        </p>
      ) : null}

      <div className="flex gap-1.5 border-t border-line bg-bg-body px-4.5 py-2.5">
        {classificationEnabled ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setClassifyDialogOpen(true)}
            disabled={!graphActionsEnabled}
            className="flex-1 text-12"
            data-testid="tag-modify-classification-button"
          >
            {classification ? 'Modify classification' : 'Set classification'}
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="outline"
          onClick={() => void handleToggleArchive()}
          disabled={archiving || !graphActionsEnabled || !onToggleArchive}
          className="flex-1 text-12"
          title={
            archived
              ? 'Restore this tag to the live views'
              : 'Mark this tag as not relevant — it moves to the Archived tab and stops counting as a problem or warning'
          }
          data-testid="tag-archive-button"
        >
          {archiving ? (
            <>
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
              {archived ? 'Unarchiving…' : 'Archiving…'}
            </>
          ) : archived ? (
            'Unarchive'
          ) : (
            'Archive'
          )}
        </Button>
        {onDelete ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(tag)}
            className="flex-1 border-st-down/40 text-12 text-st-down hover:bg-st-down/10 hover:text-st-down"
          >
            Delete
          </Button>
        ) : null}
      </div>

      {graphNodeId ? (
        <TagClassificationDialog
          open={graphActionsEnabled && classificationEnabled && classifyDialogOpen}
          onOpenChange={setClassifyDialogOpen}
          tag={graphIdentityTag}
          graphNodeId={graphNodeId}
          classification={classification}
          observedStates={observedStates}
          onSaved={() => onClassificationSaved?.()}
        />
      ) : null}
    </aside>
  )
}

export default TagDetailPanel
